import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import { type FieldErrors, type TopicsState, type GeneralTopicItem, type UrlGroup } from "./useTaskCreation";
import { CostEstimate } from "./CostEstimate";

export function FixedRelevantTopicSection(props: {
  group: UrlGroup;
  topicsState: TopicsState;
  groupErrors: FieldErrors;
  disabled: boolean;
  onChangeAnchorsText: (value: string) => void;
  onSetUniqueArticlesCount: (value: number) => void;
  onProcess: () => void;
  onResetToTextarea: () => void;
  onRetryTopics: () => void;
  onSetTopicRow: (topicIndex: number, value: string) => void;
}) {
  const { group, topicsState, groupErrors } = props;
  const isProcessing = topicsState.kind === "loading";
  const topics: GeneralTopicItem[] = topicsState.kind === "loaded" ? topicsState.topics : [];
  const topicOptions = topics.map((topic) => topic.topic);
  const topicLabels =
    group.anchorInputMode.mode === "fixed-topics"
      ? group.anchorInputMode.topics.map((_topic, index) => `Тема ${index + 1}`)
      : [];
  const estimate = CostEstimate.fixedRelevantTopicGroupEstimate(group.uniqueArticlesCount);

  return (
    <Box mb={2}>
      <TextField
        label="Анкоры"
        placeholder={"anchor,count\nbest product,3\ncheap service;5"}
        value={group.anchorsText}
        error={!!groupErrors.anchors}
        helperText={
          groupErrors.anchors ??
          "По одному на строку: anchor,количество публикаций. Анкоры — метаданные ссылок, распределяются по статьям."
        }
        disabled={props.disabled}
        onChange={(event) => props.onChangeAnchorsText(event.target.value)}
        multiline
        minRows={4}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      <TextField
        label="Количество уникальных статей"
        type="number"
        value={group.uniqueArticlesCount === 0 ? "" : group.uniqueArticlesCount}
        error={!!groupErrors.uniqueArticlesCount}
        helperText={groupErrors.uniqueArticlesCount ?? "Стоимость не зависит от числа анкоров"}
        disabled={props.disabled}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          props.onSetUniqueArticlesCount(Number.isNaN(parsed) || parsed < 0 ? 0 : parsed);
        }}
        inputProps={{ min: 1 }}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {estimate !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ориентировочная стоимость генерации: {CostEstimate.formatEstimate(estimate.uniqueArticles, estimate.cost)}
        </Typography>
      )}

      {group.anchorInputMode.mode === "fixed-topics" ? (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2">Темы уникальных статей ({group.anchorInputMode.topics.length})</Typography>
            <Button size="small" variant="text" disabled={props.disabled} onClick={props.onResetToTextarea}>
              ← Изменить анкоры
            </Button>
          </Box>
          {groupErrors.topics && <Alert severity="error" sx={{ mb: 1 }}>{groupErrors.topics}</Alert>}
          {topicsState.kind === "error" && (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              action={
                <Button color="inherit" size="small" onClick={props.onRetryTopics}>
                  Повторить
                </Button>
              }
            >
              {topicsState.message}
            </Alert>
          )}
          {group.anchorInputMode.topics.map((topic, index) => (
            <Autocomplete
              key={index}
              freeSolo
              options={topicOptions}
              value={topic}
              disabled={props.disabled}
              size="small"
              sx={{ mb: 1 }}
              onChange={(_error, newValue) => props.onSetTopicRow(index, newValue ?? "")}
              onInputChange={(_error, newValue) => props.onSetTopicRow(index, newValue)}
              renderInput={(parameters) => <TextField {...parameters} label={topicLabels[index]} />}
            />
          ))}
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            size="small"
            disabled={props.disabled || isProcessing}
            onClick={props.onProcess}
          >
            {isProcessing ? <><CircularProgress size={14} sx={{ mr: 1 }} />Обработка…</> : "Обработать"}
          </Button>
          {topicsState.kind === "error" && (
            <Alert
              severity="error"
              sx={{ mt: 1 }}
              action={
                <Button color="inherit" size="small" onClick={props.onRetryTopics}>
                  Повторить
                </Button>
              }
            >
              {topicsState.message}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
